import type { ArtifactTab } from '@/features/artifacts/tabs';
import { PAST_VIDEOS_BRIEF_PARAM, PAST_VIDEOS_CHANGE_PARAM, PAST_VIDEOS_CREATOR_PARAM, PAST_VIDEOS_TAB } from './links';

/**
 * Artifacts' Past videos tab, narrowed (`?brief=<documentId>`, `?creatorKey=<platform>:<platformId>`, and with the
 * brief, `?change=<suggestionId>`: score round 7, E2). Artifacts' own address validator (`features/artifacts/search.ts`)
 * doesn't name these: the router keeps every parameter it doesn't validate in the address and in the route's search, so
 * the tab reads them from there with this plain validator (no zod: it ships with the entry chunk's routes). Pure.
 */
export interface PastVideosNarrowSearch {
  brief?: string;
  creator?: string;
  /** One applied change of that brief (its suggestion's id): only the groups whose answers led to it. */
  change?: string;
}

/** A value the router may have read as a number (`?brief=123`), as text; else nothing. */
const textOf = (value: unknown): string | undefined =>
  typeof value === 'string' ? value.trim() : typeof value === 'number' && Number.isFinite(value) ? String(value) : undefined;

/** A suggestion's id as the API takes `change` (a uuid; any other shape it refuses). */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * As the API takes them: a brief's id up to 64 characters, a creator as `<platform>:<id>` (platform in lower case),
 * and a change as a uuid, only beside its brief (score round 7, E2: the brief's chip is its one way off).
 */
export function parsePastVideosNarrow(search: Record<string, unknown>): PastVideosNarrowSearch {
  const given = textOf(search[PAST_VIDEOS_BRIEF_PARAM]);
  const brief = given && given.length <= 64 && /^[\w-]+$/.test(given) ? given : undefined;
  const raw = textOf(search[PAST_VIDEOS_CREATOR_PARAM]);
  const at = raw ? raw.indexOf(':') : -1;
  const platform = raw && at > 0 ? raw.slice(0, at).toLowerCase() : '';
  const id = raw && at > 0 ? raw.slice(at + 1).trim() : '';
  const creator = /^[a-z]{2,20}$/.test(platform) && id && id.length <= 200 ? `${platform}:${id}` : undefined;
  const change = brief ? textOf(search[PAST_VIDEOS_CHANGE_PARAM]) : undefined;
  return {
    ...(brief ? { brief } : {}),
    ...(creator ? { creator } : {}),
    ...(change && UUID.test(change) ? { change: change.toLowerCase() } : {}),
  };
}

/** The creator profile's target from a creator key (`youtube:UC…`: split at the first `:`); null when it isn't one. */
export function profileOf(creatorKey: string | null | undefined): { platform: string; platformId: string } | null {
  const at = creatorKey ? creatorKey.indexOf(':') : -1;
  if (!creatorKey || at <= 0 || at === creatorKey.length - 1) return null;
  return { platform: creatorKey.slice(0, at), platformId: creatorKey.slice(at + 1) };
}

/**
 * Artifacts' tabs with Past videos (Kazzy, 16 Oct 2026: its home is Artifacts): after the kinds her role has, before
 * the "Coming soon" ones, for a role with the Content page (as the old page had it). Pure.
 */
export function withPastVideosTab(tabs: readonly ArtifactTab[], contentOn: boolean): ArtifactTab[] {
  if (!contentOn || tabs.includes(PAST_VIDEOS_TAB)) return [...tabs];
  const soon = tabs.findIndex((t) => t === 'influencer_batch' || t === 'report');
  const at = soon < 0 ? tabs.length : soon;
  return [...tabs.slice(0, at), PAST_VIDEOS_TAB, ...tabs.slice(at)];
}

/** The tab a link asks for, as the page opens it: one of hers (Past videos included), else All. Pure. */
export const openTabIn = (kind: unknown, tabs: readonly ArtifactTab[]): ArtifactTab =>
  typeof kind === 'string' && (tabs as readonly string[]).includes(kind) ? (kind as ArtifactTab) : 'all';

/**
 * The tab's address search as she leaves it for a group (E2), `kind` first (`?kind=past_videos&q=bree&brief=…`), as
 * `readPastTab` takes it for "← Artifacts": the router writes the keys in the order they came (`?q=bree&kind=…` after
 * a search on All), so `kind` is moved to the front, the rest kept as written. Pure.
 */
export function pastTabSearch(searchStr: string): string {
  const rest = [...new URLSearchParams(searchStr)].filter(([key]) => key !== 'kind');
  return `?${new URLSearchParams([['kind', PAST_VIDEOS_TAB], ...rest]).toString()}`;
}

const NARROW: readonly string[] = [PAST_VIDEOS_BRIEF_PARAM, PAST_VIDEOS_CREATOR_PARAM, PAST_VIDEOS_CHANGE_PARAM];

/** What a way to the tab from Artifacts' other tabs leaves behind: the tab, its step, the open panel and its part. */
const LEFT_BEHIND: readonly string[] = ['kind', 'step', 'id', 'part', ...NARROW];

/** An Artifacts address's search, her campaign and search kept as written, without what `LEFT_BEHIND` names. */
const keptFrom = (searchStr: string) => [...new URLSearchParams(searchStr)].filter(([key]) => !LEFT_BEHIND.includes(key));

/**
 * The Past videos tab's address search from Artifacts' other tabs (score round 4, E1: the search's row and the row
 * after All's cards), `kind` first: her campaign and search kept as written; another tab's step, the open panel and
 * the tab's own narrowing left behind (a door that narrows it says so again). Pure.
 */
export function pastTabFrom(searchStr: string): string {
  return `?${new URLSearchParams([['kind', PAST_VIDEOS_TAB], ...keptFrom(searchStr)]).toString()}`;
}

/**
 * All's own address search, as a card on All carries it for its group's "← Artifacts" (score round 4, E1's check: a
 * card opened from All comes back to All, as Back does): her campaign kept, the open panel left behind; `?` alone is
 * All with every campaign. `readPastTab` takes it. Pure.
 */
export const allTabFrom = (searchStr: string): string => `?${new URLSearchParams(keptFrom(searchStr)).toString()}`;

/**
 * Artifacts' address on a change of tab: the Past videos tab's narrowing (`brief`, `creatorKey`, and score round 7's
 * `change`) goes, so the tab opens on every group when she comes back, unless a door narrows it again (round 4b's walk:
 * the brief's chip came back after a visit to Contracts). Pure.
 */
export const withoutPastNarrow = <T extends object>(search: T): T => Object.fromEntries(Object.entries(search).filter(([key]) => !NARROW.includes(key))) as T;
